import { useEffect, useState } from "react";

export const DecisionProcessVisual = () => {
  const [hubVisible, setHubVisible] = useState(false);
  const [orbitsVisible, setOrbitsVisible] = useState(false);
  const [nodesVisible, setNodesVisible] = useState<boolean[]>([false, false, false, false]);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setHubVisible(true), 100),
      setTimeout(() => setOrbitsVisible(true), 300),
      setTimeout(() => setNodesVisible([true, false, false, false]), 500),
      setTimeout(() => setNodesVisible([true, true, false, false]), 700),
      setTimeout(() => setNodesVisible([true, true, true, false]), 900),
      setTimeout(() => setNodesVisible([true, true, true, true]), 1100),
    ];
    
    const stepInterval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 3000);

    return () => {
      timers.forEach(clearTimeout);
      clearInterval(stepInterval);
    };
  }, []);

  const steps = [
    { label: "Kontext", sublabel: "erfassen" },
    { label: "Optionen", sublabel: "bewerten" },
    { label: "Trade-offs", sublabel: "abwägen" },
    { label: "Commitment", sublabel: "dokumentieren" },
  ];

  // Exact positions for perfect symmetry (no floating point from trig)
  const nodePositions = [
    { x: 160, y: 60 },   // top (index 0)
    { x: 260, y: 160 },  // right (index 1)
    { x: 160, y: 260 },  // bottom (index 2)
    { x: 60, y: 160 },   // left (index 3)
  ];

  // CSS translate offsets (relative to center, matching SVG)
  const nodeTranslates = [
    { x: 0, y: -100 },
    { x: 100, y: 0 },
    { x: 0, y: 100 },
    { x: -100, y: 0 },
  ];

  return (
    <div className="relative w-full h-full min-h-[400px] flex items-center justify-center">
      {/* Central hub */}
      <div 
        className="absolute w-24 h-24 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 border-2 border-primary/30 flex items-center justify-center"
        style={{
          animation: hubVisible ? 'hub-bounce 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards' : 'none',
          transform: hubVisible ? undefined : 'scale(0)',
        }}
      >
        <div className="absolute w-10 h-10 rounded-full bg-primary/40 animate-[pulse_3s_ease-in-out_infinite]" />
        <div className="absolute w-5 h-5 rounded-full bg-primary/80" />
        <div className="absolute w-20 h-20 rounded-full border-2 border-dashed border-primary/40 animate-[spin_20s_linear_infinite]" />
      </div>

      {/* Orbital paths */}
      <svg 
        className="absolute w-[320px] h-[320px]"
        viewBox="0 0 320 320"
      >
        {/* Outer orbit */}
        <circle
          cx="160" cy="160" r="140"
          fill="none"
          stroke="hsl(var(--primary) / 0.18)"
          strokeWidth="1.5"
          strokeDasharray="8 4"
          className="animate-[spin_60s_linear_infinite_reverse]"
          style={{ 
            transformOrigin: 'center',
            opacity: orbitsVisible ? 1 : 0,
            transition: 'opacity 0.5s ease',
          }}
        />
        
        {/* Inner orbit */}
        <circle
          cx="160" cy="160" r="100"
          fill="none"
          stroke="hsl(var(--primary) / 0.25)"
          strokeWidth="1.5"
          strokeDasharray="628"
          style={{
            strokeDashoffset: orbitsVisible ? 0 : 628,
            transition: 'stroke-dashoffset 1.2s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        />
        
        {/* Connection lines - exact integer coordinates */}
        {nodePositions.map((pos, index) => (
          <line
            key={index}
            x1="160" y1="160"
            x2={pos.x} y2={pos.y}
            stroke={`hsl(var(--primary) / ${activeStep === index ? 0.5 : 0.15})`}
            strokeWidth={activeStep === index ? 2.5 : 1.5}
            className="transition-all duration-500"
            style={{
              opacity: nodesVisible[index] ? 1 : 0,
              transition: 'opacity 0.3s ease, stroke 0.5s ease, stroke-width 0.5s ease',
            }}
          />
        ))}
      </svg>

      {/* Step nodes */}
      {steps.map((step, index) => {
        const { x, y } = nodeTranslates[index];
        const isActive = activeStep === index;
        
        return (
          <div
            key={index}
            className="absolute flex flex-col items-center"
            style={{
              '--tx': `${x}px`,
              '--ty': `${y}px`,
              animation: nodesVisible[index] 
                ? `node-bounce-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards`
                : 'none',
              transform: nodesVisible[index] ? undefined : `translate(${x}px, ${y}px) scale(0)`,
              opacity: nodesVisible[index] ? undefined : 0,
            } as React.CSSProperties}
          >
            <div 
              className={`relative w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-500 ${
                isActive 
                  ? 'bg-primary/30 border-primary/50 scale-110 shadow-lg shadow-primary/25' 
                  : 'bg-card/80 border-primary/20 scale-100'
              } border-2 backdrop-blur-sm`}
            >
              <span 
                className={`text-sm font-bold transition-colors duration-500 ${
                  isActive ? 'text-primary' : 'text-foreground/70'
                }`}
              >
                {index + 1}
              </span>
              
              {isActive && (
                <div className="absolute -inset-1.5 rounded-xl border-2 border-primary/40 animate-[pulse_2s_ease-in-out_infinite]" />
              )}
            </div>
            
            <div 
              className={`mt-2.5 text-center transition-all duration-500 ${
                isActive ? 'opacity-100' : 'opacity-70'
              }`}
            >
              <p className={`text-xs font-semibold transition-colors duration-500 ${
                isActive ? 'text-foreground' : 'text-foreground/70'
              }`}>
                {step.label}
              </p>
              <p className={`text-[10px] transition-colors duration-500 ${
                isActive ? 'text-muted-foreground' : 'text-muted-foreground/60'
              }`}>
                {step.sublabel}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};
